import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Image from 'next/image';
import Link from 'next/link';
import { MdLockOutline } from 'react-icons/md';
import TextArea from 'ui/components/atoms/TextArea';
import Input from '../../atoms/Input';
import Dropdown from '../../atoms/Dropdown';
import BreadCrumbs, { IBreadcrumbs } from '../../molecules/Breadcrumbs';
import Dropzone from '../../atoms/DropzoneContactUs';
import ArrowForward from '../../atoms/Icons/ArrowForward';
import Checkbox from '../../atoms/CheckBox';
import Phone from '../../atoms/Icons/PhoneIcons';
import { ContactUsImage } from '../../../assets/images';
import useIsNativeView from '../../../hooks/useIsNativeView';
import BodyMedium from '../../atoms/Text/BodyMedium';
import { IRootState } from '../../../store';
import validateFullName from '../../../helpers/utils/validateFullName';
import { useRouter } from '../../../hooks/useCustomRouter';
import { getProvince } from '../../../services/geolocation';
import { getCity } from '../../../services/geolocation';
import TermsDialog from '../../atoms/TermsDialog';
import { validateEmail } from '../../../helpers/utils';

type Province = {
  id: number;
  isocode: string;
  name: string;
  googleMapName: string;
};

type City = {
  id: number;
  isocode: string;
  name: string;
  googleMapName: string;
  code: string;
};

type ContactUsForm = {
  typeQuestion?: string;
  question?: string;
  fullName?: string;
  province?: string;
  city?: string;
  phoneNumber?: string;
  email?: string;
  address?: string;
  note?: string;
  postcode?: string;
  attachment?: File;
};

const ContactUsPageTemplate = ({ breadcrumbItems = [] }: { breadcrumbItems: IBreadcrumbs[] }) => {
  const router = useRouter();
  const { isMobileScreen } = useSelector((state: IRootState) => state.page);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [agreed, setAgreed] = useState(false);
  const [checkFormInquiry, setCheckFormInquiry] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const questionTypes = [
    'Pertanyaan Terkait Penjualan',
    'Pertanyaan Terkait Servis',
    'Pertanyaan Terkait Produk',
    'Pertanyaan Umum',
    'Komplain Customer',
  ];

  // Check if page is for mobile native
  const isNativeView = useIsNativeView();

  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

  // FORM
  const [submit, setSubmit] = useState<ContactUsForm>({
    typeQuestion: '',
    question: '',
    fullName: '',
    province: '',
    city: '',
    phoneNumber: '',
    email: '',
    address: '',
    note: '',
    postcode: '',
    attachment: null,
  });

  const [errorInput, setErrorInput] = useState({
    fullName: null,
    phoneNumber: null,
    province: null,
    email: null,
  });

  useEffect(() => {
    populateProvinces();
  }, []);

  useEffect(() => {
    if (provinces.length === 0 && !submit.province) return;
    const getProvinceItem = provinces.find((province) => province.name === submit.province);
    if (getProvinceItem) populateCity(getProvinceItem.isocode);
  }, [submit.province]);

  useEffect(() => {
    if (agreed && submit.question !== '' && submit.typeQuestion !== '') setCheckFormInquiry(true);
    else setCheckFormInquiry(false);
  }, [agreed, submit.question, submit.typeQuestion]);

  const populateProvinces = async () => {
    try {
      const fetchPronvinces: { data: Province[] } = await getProvince();
      setProvinces(fetchPronvinces.data);
    } catch (error) {
      console.log(error);
    }
  };

  const populateCity = async (isocode: string) => {
    try {
      const fetchCities: { data: City[] } = await getCity(isocode);
      setCities(fetchCities.data);
    } catch (error) {
      console.log(error);
    }
  };

  const validateInputs = (value, category) => {
    let error: string | null = null;

    // Check if fullName is contains just alphabet chars
    if (category === 'fullName' && !validateFullName(value)) {
      error = 'Hanya boleh diisi dengan huruf';
    }

    // Check if phone number is not valid
    if (category === 'phoneNumber' && value.toString().length < 9) {
      error = 'Mohon Masukkan Nomor Handphone Yang Valid';
    }

    if (category === 'email' && !validateEmail(value)) {
      error = 'Mohon Masukkan Email Yang Valid';
    }

    setErrorInput({ ...errorInput, [category]: error });
  };

  const handleChange = (e, category) => {
    if (category === 'typeQuestion') {
      // const newValue = e.target.value;
      const newValue = e;
      setSubmit((prevState) => ({
        ...prevState,
        typeQuestion: newValue,
      }));
    }

    if (category === 'question') {
      setSubmit((prevState) => ({
        ...prevState,
        question: e?.target?.value,
      }));
    }

    if (category === 'fullName') {
      setSubmit((prevState) => ({
        ...prevState,
        fullName: e?.target?.value,
      }));
    }

    if (category === 'phoneNumber') {
      const newValue = e.target.value;

      if (newValue.startsWith(0)) {
        return;
      }

      setSubmit((prevState) => ({
        ...prevState,
        phoneNumber: newValue,
      }));
    }

    if (category === 'province') {
      // const newValue = e.target.value;
      const newValue = e;
      setSubmit((prevState) => ({
        ...prevState,
        province: newValue,
        city: '',
      }));
    }

    if (category === 'city') {
      // const newValue = e.target.value;
      const newValue = e;
      setSubmit((prevState) => ({
        ...prevState,
        city: newValue,
      }));
    }

    if (category === 'email') {
      const newValue = e.target.value;
      setSubmit((prevState) => ({
        ...prevState,
        email: newValue,
      }));
    }

    if (category === 'postcode') {
      const newValue = e.target.value;
      setSubmit((prevState) => ({
        ...prevState,
        postcode: newValue,
      }));
    }

    if (category === 'note') {
      const newValue = e.target.value;
      setSubmit((prevState) => ({
        ...prevState,
        note: newValue,
      }));
    }

    if (category === 'address') {
      const newValue = e.target.value;
      setSubmit((prevState) => ({
        ...prevState,
        address: newValue,
      }));
    }

    if (category === 'attachment') {
      const file: File = e;
      setSubmit((prevState) => ({
        ...prevState,
        attachment: file,
      }));
    }

    // Validate input
    validateInputs(e.target ? e.target.value : e, category);
  };

  const handleSubmit = (e) => {
    // TODO handle API (does not exist yet)
    e?.preventDefault();
    if (errorInput.fullName || errorInput.phoneNumber || errorInput.province || errorInput.email)
      return;
  };

  // Check if form is disabled
  // const checkFormInquiry = !submit.typeQuestion || !submit.question;

  return (
    <React.Fragment>
      {/* Breadcrumb for web screen */}
      {!isNativeView && (
        <>
          <BreadCrumbs isMobileScreen={false} items={breadcrumbItems} />
          <div className="container flex justify-start items-center w-full border-b border-reliableBlack10 pt-2 pb-2 sticky top-[60px] md:top-[121px] z-30 bg-white md:hidden">
            <button className="h-full rotate-180 " onClick={() => router.back()}>
              <ArrowForward size={24} color="#3D4043" />
            </button>
          </div>
        </>
      )}

      <section className="container xl:px-0 md:max-w-[818px] w-full mt-4 lg:mt-0 lg:mb-[147px]">
        <div className="pt-5 flex flex-col gap-4 lg:px-4 lg:mb-24 lg:mt-20">
          <div className="flex justify-between items-center">
            <div className="hidden md:inline-block max-w-[418px]">
              <h1 className="flex-1 text-base lg:text-2xl font-bold uppercase text-reliableBlack mb-2">
                HUBUNGI KAMI
              </h1>
              <p className="text-base capitalize text-[#7B7979] lg:text-sm">
                Silakan isi form di bawah ini jika Anda memiliki pertanyaan terkait produk, layanan,
                atau pertanyaan lain. Untuk memulai, silakan pilih jenis pertanyaan Anda agar kami
                dapat melayani Anda dengan lebih baik.
              </p>
            </div>
            <div className="flex justify-between md:justify-end items-center flex-1 gap-2">
              <Image width={160} height={160} src={ContactUsImage.src} alt="contact Us" />
              <div className="flex justify-between items-end flex-col gap-2.5">
                <a
                  id="nav-call"
                  href={`${process.env.NEXT_PUBLIC_URL}/hubungi-kami`}
                  className="flex text-supportiveRed items-center gap-1"
                >
                  <Phone size={28} fill="#CE181E" />
                  <p className="text-2xl font-bold">1-500 898</p>
                </a>
                <BodyMedium className="text-base font-medium sm:text-sm">
                  Auto2000 Call Center
                </BodyMedium>
              </div>
            </div>
          </div>
          <div className="md:hidden">
            <h1 className="flex-1 text-base font-bold uppercase text-reliableBlack md:inline-block">
              HUBUNGI KAMI
            </h1>
            <p className="text-xs font-medium leading-relaxed text-reliableBlack lg:text-sm">
              Silakan isi form di bawah ini jika Anda memiliki pertanyaan terkait produk, layanan,
              atau pertanyaan lainnya terkait Auto2000. Untuk memulai, silakan pilih jenis
              pertanyaan agar layanan terbaik kami dapat Anda nikmati
            </p>
          </div>
        </div>

        {/* Form */}
        <form className="mt-6 lg:px-4" onSubmit={handleSubmit}>
          <div className={`grid grid-cols-1 md:grid-cols-2 border-t border-reliableBlack20`}>
            {/* WHAT QUESTION */}
            <div className="mb-[18px] lg:mb-4 mt-4 md:col-span-1">
              <Dropdown
                withInput={true}
                // label="Jenis Pertanyaan"
                // labelClassname="block text-[#666666] text-[13px] font-medium mb-[10px] lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                additionalClassname="!border-[#5f5f5f] placeholder:!text-reliableBlack30 !py-[10px] h-[52px] placeholder:text-[15px]"
                options={questionTypes}
                optionLabel="name"
                selectedOption={submit.typeQuestion}
                onSelect={(e) => handleChange(e, 'typeQuestion')}
                placeholder="Pertanyaan Umum"
                disabled={false}
                id={!isMobileScreen ? 'dd-provinsi' : 'mobile-dd-provinsi'}
              />
            </div>
          </div>

          <div className={`py-2`}>
            <h2 className="flex-1 text-base font-bold uppercase text-reliableBlack mb-6">
              INFORMASI DATA PRIBADI
            </h2>
            {/* INPUT FULLNAME */}
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6`}>
              <div className={`mb-[18px] lg:mb-0`}>
                <Input
                  id={isMobileScreen ? `mobile-input-full-name` : `input-full-name`}
                  type="text"
                  label="Nama Lengkap"
                  placeholder="Nama Lengkap"
                  size="large"
                  labelClassName="block text-[#666666] text-xs font-medium mb-2.5 lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  inputClassName="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 max-h-fit lg:max-h-fit"
                  danger={errorInput.fullName}
                  error={errorInput.fullName}
                  value={submit.fullName}
                  onChange={(e) => handleChange(e, 'fullName')}
                  disabled={false}
                />
              </div>

              {/* PHONE NUMBER */}
              <div className="mb-[18px] lg:mb-0">
                <Input
                  id={isMobileScreen ? `mobile-input-phone-number` : `input-phone-number`}
                  type="number"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  labelClassName="block text-[#666666] text-xs font-medium mb-[10px] lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  value={submit.phoneNumber}
                  label="Nomor Handphone"
                  placeholder="XXX-XXX-XXX"
                  size="large"
                  min={1}
                  // containerClassName="w-[50px] w-full"
                  inputClassName="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 max-h-fit lg:max-h-fit"
                  inputWrapperClassName={'bg-reliableBlack3 lg:bg-reliableBlack3'}
                  danger={errorInput.phoneNumber}
                  error={errorInput.phoneNumber}
                  icon={
                    <span className="text-base text-reliableBlack font-medium transform pl-4">
                      +62
                    </span>
                  }
                  iconClassName={'!border-[#5f5f5f]'}
                  iconPosition="left"
                  onChange={(e) => handleChange(e, 'phoneNumber')}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  disabled={false}
                />
              </div>

              {/* INPUT EMAIL */}
              <div className={`mb-[18px] lg:mb-0`}>
                <Input
                  id={isMobileScreen ? `mobile-input-full-name` : `input-full-name`}
                  type="text"
                  label="Email"
                  placeholder="Masukan Email Anda"
                  size="medium"
                  labelClassName="block text-[#666666] text-xs font-medium mb-2.5 lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  inputClassName="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 max-h-fit lg:max-h-fit"
                  value={submit.email}
                  onChange={(e) => handleChange(e, 'email')}
                  disabled={false}
                  danger={errorInput.email}
                  error={errorInput.email}
                />
              </div>

              {/* DROPDOWN PROVINCE */}
              <div className={`mb-[18px] lg:mb-0`}>
                <Dropdown
                  withInput
                  label="Provinsi"
                  labelClassname="block text-[#666666] text-xs font-medium mb-[10px] lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  additionalClassname="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 lg:max-h-fit"
                  options={provinces.map((province) => province.name)}
                  optionLabel="name"
                  selectedOption={submit.province}
                  onSelect={(e) => handleChange(e, 'province')}
                  placeholder="Pilih Provinsi"
                  disabled={false}
                  id={!isMobileScreen ? 'dd-provinsi' : 'mobile-dd-provinsi'}
                />
              </div>

              {/* INPUT ADDRESS */}
              <div className={`mb-[18px] lg:mb-0`}>
                <Input
                  id={isMobileScreen ? `mobile-input-full-name` : `input-full-name`}
                  type="text"
                  label="Alamat Rumah"
                  placeholder="Alamat Rumah"
                  size="medium"
                  labelClassName="block text-[#666666] text-xs font-medium mb-2.5 lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  inputClassName="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 max-h-fit lg:max-h-fit"
                  value={submit.address}
                  onChange={(e) => handleChange(e, 'address')}
                  disabled={false}
                />
              </div>

              {/* DROPDOWN KOTA */}
              <div className={`mb-[18px] lg:mb-0`}>
                <Dropdown
                  withInput
                  label="Kota"
                  labelClassname="block text-[#666666] text-xs font-medium mb-[10px] lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  additionalClassname="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 max-h-fit lg:max-h-fit"
                  options={cities.map((city) => city.name)}
                  optionLabel="name"
                  selectedOption={submit.city}
                  onSelect={(e) => handleChange(e, 'city')}
                  placeholder="Pilih Kota"
                  disabled={false}
                  id={!isMobileScreen ? 'dd-provinsi' : 'mobile-dd-provinsi'}
                />
              </div>

              {/* CATATAN */}
              <div className="mb-[18px] lg:mb-5">
                <TextArea
                  label="Catatan"
                  size="large"
                  value={submit.note}
                  onChange={(e) => handleChange(e, 'note')}
                  containerClassName="mt-2"
                  inputClassName="!border-[#5f5f5f] block py-4 px-5 text-[#666666] text-[13px] font-medium mb-[10px] lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  labelClassName="text-xs"
                  placeholder="Tulis Catatan Anda di sini"
                  maxLength={250}
                  showIndicator={undefined}
                />
                <span className="flex justify-end text-xs">{submit.note.length}/250</span>
              </div>

              {/* POSTCODE */}
              <div className={`mb-[18px] lg:mb-0`}>
                <Input
                  id={isMobileScreen ? `mobile-input-full-name` : `input-full-name`}
                  type="number"
                  label="Kode Pos"
                  placeholder="Kode Pos"
                  size="medium"
                  labelClassName="block text-[#666666] text-xs font-medium mb-2.5 lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  inputClassName="!border-[#5f5f5f] px-5 py-3.5 text-reliableBlack bg-reliableBlack3 placeholder:text-[#777777] lg:text-reliableBlack60 max-h-fit lg:max-h-fit"
                  value={submit.postcode}
                  onChange={(e) => handleChange(e, 'postcode')}
                  disabled={false}
                />
              </div>
            </div>
          </div>

          <div className={`grid grid-cols-1`}>
            <div className="mb-[18px] lg:mb-5">
              <p className="text-xs leading-relaxed text-reliableBlack70 mb-2 lg:text-[13px]">
                Lampiran (Jika Ada)
              </p>
              <p className="text-xs italic leading-relaxed text-reliableBlack50 lg:text-xs">
                Tidak ada file yang dipiih
              </p>
              <Dropzone
                containerClassName="col-span-full mt-2"
                onChange={(file) => {
                  // handleUpload(file);
                  handleChange(file, 'attachment');
                }}
                initialValue={''}
                onRemove={() => setSubmit((prev) => ({ ...prev, attachment: null }))}
              />
            </div>
            {/* QUESTION */}
            <div className="mb-[18px] lg:mb-5">
              <h2 className="flex-1 text-base font-bold uppercase text-reliableBlack mb-1">
                PERTANYAAN ANDA
              </h2>
              <p className="text-xs leading-7 text-reliableBlack py-2 lg:text-sm">
                Tulis pertanyaan Anda dan kami akan segera mengabari Anda secepatnya. Jika Anda
                tidak mendapat respon dari kami, silakan hubungi 1-500 898 untuk urusan mendesak.
              </p>
              <div className="md:w-1/2">
                <TextArea
                  size="large"
                  value={submit.question}
                  onChange={(e) => handleChange(e, 'question')}
                  containerClassName="mt-2"
                  inputClassName="!border-[#5f5f5f] block py-4 px-5 text-[#666666] text-[13px] font-medium mb-[10px] lg:text-reliableBlack70 lg:text-sm lg:mb-2"
                  placeholder="Tulis Catatan Anda di sini"
                  maxLength={250}
                  showIndicator={undefined}
                />
                <span className="flex justify-end text-xs">{submit.question.length}/250</span>
              </div>
            </div>
          </div>

          {/** SETUJU SYARAT DAN KETENTUAN */}
          <div className="my-2 flex flex-col gap-1">
            {/* CheckBox */}
            <div id="branch-cb-term" className={`ml-0.5 mb-4 lg:mb-6`}>
              <Checkbox
                labelClassname="flex gap-4 text-sm font-normal"
                checkboxTestId={'checkbox-term-condition'}
                label={
                  <p className="ml-0.5">
                    Saya telah membaca dan menyetujui{' '}
                    <span
                      id={'link-syarat'}
                      className="text-hyperLink font-medium"
                      onClick={(e) => {
                        if (isNativeView) {
                          e.preventDefault();
                        }
                        // handleGoToTermAndCondition();
                        setOpenModal(true);
                      }}
                    >
                      Syarat dan Ketentuan
                    </span>{' '}
                    Auto2000
                  </p>
                }
                checked={agreed}
                onChange={(e) => handleChange(e, 'isChecked')}
                onClick={() => setAgreed((prev) => !prev)}
              />
            </div>

            {/* Privacy policy */}
            <div
              className="flex items-center gap-4 mb-5 lg:mb-10"
              // id="branch-cb-policy-plp"
              // className={`flex items-center gap-4 mb-5 lg:mb-10 ${
              //   containerDropdown ? containerDropdown : ''
              // } ${!error && isInquiryMobile ? 'pb-20 md:pb-0' : ''}`}
            >
              <MdLockOutline color={'#7B7979'} size={isMobileScreen ? 20 : 22} />
              <p className="flex-1 text-reliableBlack font-normal text-sm">
                Data pribadi Anda aman bersama kami.{' '}
                <Link
                  id={'link-kebijakan'}
                  className="block text-hyperLink font-medium lg:inline-block"
                  href={`${PUBLIC_URL}/pemberitahuan-privasi`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    if (isNativeView) {
                      e.preventDefault();
                    }
                    // handleGoToPrivacyPolicy();
                  }}
                >
                  Pemberitahuan Privasi
                </Link>
              </p>
            </div>
          </div>

          {/** Terms Dialog */}
          <TermsDialog
            openModal={openModal}
            agree={() => setAgreed(true)}
            disagree={() => setAgreed(false)}
            closeModal={() => setOpenModal(false)}
          />

          {/* Suubmit button */}
          <div className="my-0 md:w-[290px]">
            <button
              type="submit"
              disabled={!checkFormInquiry}
              className={`hover:cursor-pointer ${
                checkFormInquiry ? 'bg-reliableBlack' : 'bg-reliableBlack20'
              }  w-full text-[#FFFEFE] p-4`}
            >
              <p className="text-base font-bold uppercase text-black90">Submit</p>
            </button>
          </div>
        </form>
      </section>

      {/* Breadcrumb for Mobile Screen */}
      <BreadCrumbs isMobileScreen={true} items={breadcrumbItems} />
    </React.Fragment>
  );
};

export default ContactUsPageTemplate;
